import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";

import type { UserType } from "@/types/user";

import { getGoogleOAuthTokens } from "@/api/auth";
import env from "@/valid-env";
import { useMutation } from "@tanstack/react-query";

function getGoogleOAuthURL(userType: UserType) {
	const rootUrl = "https://accounts.google.com/o/oauth2/v2/auth";

	const options = {
		redirect_uri: location.origin + location.pathname,
		client_id: env.VITE_GOOGLE_CLIENT_ID,
		access_type: "offline",
		response_type: "code",
		prompt: "consent",
		state: userType,
		scope: [
			"https://www.googleapis.com/auth/userinfo.profile",
			"https://www.googleapis.com/auth/userinfo.email"
		].join(" ")
	};

	const qs = new URLSearchParams(options);
	return `${rootUrl}?${qs.toString()}`;
}

export interface GoogleOAuthTokens {
	id_token: string;
	access_token: string;
	expires_in: number;
	refresh_token: string;
	scope: string;
	token_type: string;
}
export function useGoogleOAuthSignIn() {
	const processedCodeRef = useRef(false);
	const code = typeof window !== "undefined"
		? new URLSearchParams(window.location.search).get("code") ?? ""
		: "";

	const mutation = useMutation({
		mutationFn: async ({ code }: { code: string }) => await getGoogleOAuthTokens({ code, redirect_uri: location.origin + location.pathname }),
		onError: (error: any) => {
			const message = error?.response?.data?.error_description || error?.message || "Google sign-in failed. Please try again.";
			toast.error(message);
		}
	});

	useEffect(() => {
		if (!code || processedCodeRef.current)
			return;
		processedCodeRef.current = true;
		mutation.mutate({ code });
		// Exchange the returned code once. Do not replaceState here — that
		// history write has no user gesture and Chrome will mark it skippable.
		// Successful sign-in replaces this callback URL via navigate({ replace: true }).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [code]);

	const oauthSignIn = useCallback((userType: UserType = "BUYER") => {
		window.location.href = getGoogleOAuthURL(userType);
	}, []);

	return {
		signIn: oauthSignIn,
		...mutation
	};
}
